import { useEffect, useState } from "react";
import axios from "axios";
import { ClipboardList, Loader2, Inbox, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useCampusAuth } from "@/contexts/CampusAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " Uhr";
  } catch {
    return iso;
  }
}

function fmtAmount(v, currency = "eur") {
  if (v == null) return "–";
  return `${v.toLocaleString("de-DE", { minimumFractionDigits: 2 })} ${currency === "eur" ? "€" : currency.toUpperCase()}`;
}

async function downloadWiderrufNachweis(b) {
  try {
    const res = await axios.get(`${API}/admin/bookings/${b.id}/widerruf-nachweis`, {
      withCredentials: true,
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Widerrufsverzicht-${b.participant?.last_name || "Buchung"}-${b.id.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Nachweis heruntergeladen.");
  } catch {
    toast.error("Nachweis konnte nicht erstellt werden.");
  }
}

export default function CampusBuchungen() {
  const { user } = useCampusAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    axios
      .get(`${API}/admin/bookings`, { withCredentials: true })
      .then((res) => setRows(res.data))
      .catch((err) => setError(err?.response?.data?.detail || "Buchungen konnten nicht geladen werden."));
  }, [user]);

  if (!user?.is_admin) {
    return (
      <div data-testid="bookings-no-access" className="rounded-2xl bg-cream-100 border border-cream-300 p-8 text-sand-700 text-sm">
        Dieser Bereich ist nur für Administrator:innen sichtbar.
      </div>
    );
  }

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-terra/12 text-terra">
          <ClipboardList className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <h1 className="font-display text-3xl font-normal tracking-tight text-sand-800">Kursbuchungen</h1>
      </div>
      <p className="text-sand-600 text-sm mb-8">
        Alle Buchungen inkl. Teilnehmerdaten — Kurse und Lernpakete, bezahlte sowie ausstehende SEPA/Klarna-Zahlungen (Bestätigung dauert 1–3 Werktage).
      </p>

      {error && (
        <div className="rounded-xl bg-terra/10 border border-terra/30 text-terra px-4 py-3 text-sm mb-6">{error}</div>
      )}

      {!rows && !error && (
        <div className="flex items-center gap-2 text-sand-600 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Lade Buchungen …
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="rounded-2xl bg-white border border-cream-300 p-10 text-center text-sand-600">
          <Inbox className="h-8 w-8 mx-auto mb-3 text-sand-400" strokeWidth={1.4} />
          Noch keine bezahlten Buchungen.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="rounded-2xl bg-white border border-cream-300 overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="admin-bookings-table" className="w-full text-sm">
              <thead>
                <tr className="bg-cream-100 text-left text-[11px] uppercase tracking-wider text-sand-500">
                  <th className="px-4 py-3 font-semibold">Datum</th>
                  <th className="px-4 py-3 font-semibold">Kurs</th>
                  <th className="px-4 py-3 font-semibold">Teilnehmer:in</th>
                  <th className="px-4 py-3 font-semibold">Geburtsdatum</th>
                  <th className="px-4 py-3 font-semibold">Kontakt</th>
                  <th className="px-4 py-3 font-semibold">Adresse</th>
                  <th className="px-4 py-3 font-semibold">Zahlung</th>
                  <th className="px-4 py-3 font-semibold">Widerruf</th>
                  <th className="px-4 py-3 font-semibold text-right">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const p = b.participant || {};
                  return (
                    <tr key={b.id} className="border-t border-cream-200 align-top hover:bg-cream-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sand-600">{fmtDate(b.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-sand-800 max-w-[200px]">
                        {b.kind === "ebook" && (
                          <span className="inline-flex items-center rounded-full bg-saffron/20 text-sand-800 px-2 py-0.5 text-[10.5px] font-bold mb-1" data-testid={`booking-ebook-badge-${b.id}`}>
                            Lernpaket / E-Book
                          </span>
                        )}
                        <div>{b.course_title || b.course_id || b.ebook_title || "–"}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-sand-800">
                        {b.participants?.length ? (
                          <>
                            <div>{b.organisation || "Einrichtung"}</div>
                            <div className="text-[11px] text-terra font-bold">{b.participants.length} Teilnehmer · {b.discount_pct || 0} % Rabatt</div>
                            <div className="text-[11px] text-sand-500 font-normal whitespace-normal max-w-[200px]">
                              {b.participants.map((x) => `${x.first_name} ${x.last_name}${x.course_title ? " (" + x.course_title.split(" ").slice(0, 2).join(" ") + ")" : ""}`).join(", ")}
                            </div>
                          </>
                        ) : (
                          p.first_name ? `${p.first_name} ${p.last_name || ""}` : "–"
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sand-700">{fmtDate(p.birthdate)}</td>
                      <td className="px-4 py-3 text-sand-700">
                        <div>{p.email || b.customer_email || "–"}</div>
                        <div className="text-sand-500">{p.phone || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-sand-700">
                        {p.street ? (
                          <>
                            <div>{p.street}</div>
                            <div>{p.zip_code} {p.city}</div>
                            {p.organisation && <div className="text-sand-500">{p.organisation}</div>}
                          </>
                        ) : "–"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {b.payment_status === "pending" ? (
                          <span className="inline-flex items-center rounded-full bg-sky-100 text-sky-800 px-2.5 py-0.5 text-[11px] font-bold" data-testid={`booking-pending-${b.id}`}>
                            Zahlung ausstehend (SEPA/Klarna)
                          </span>
                        ) : b.payment_method === "rechnung" ? (
                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-[11px] font-bold">
                            Rechnung · offen
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-sage/20 text-sage-dark px-2.5 py-0.5 text-[11px] font-bold">
                            Online · bezahlt
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-testid={`booking-widerruf-${b.id}`}>
                        {b.kind === "b2b" || b.kind === "jahresplan" ? (
                          <span className="inline-flex items-center rounded-full bg-cream-200 text-sand-600 px-2.5 py-0.5 text-[11px] font-bold">
                            Entfällt (B2B)
                          </span>
                        ) : b.kind === "ebook" ? (
                          <span className="inline-flex items-center rounded-full bg-cream-200 text-sand-600 px-2.5 py-0.5 text-[11px] font-bold">
                            Entfällt (Download)
                          </span>
                        ) : b.widerruf_consent ? (
                          <>
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-0.5 text-[11px] font-bold">
                              ✓ Verzicht erklärt
                            </span>
                            <div className="mt-1 text-[10.5px] text-sand-500">
                              {fmtDateTime(b.widerruf_consent_at)}
                              {b.consent_ip ? <><br />IP {b.consent_ip}</> : null}
                            </div>
                            <button
                              type="button"
                              onClick={() => downloadWiderrufNachweis(b)}
                              data-testid={`booking-widerruf-pdf-${b.id}`}
                              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-terra hover:underline"
                            >
                              <FileDown className="h-3 w-3" strokeWidth={2} /> Nachweis (PDF)
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-[11px] font-bold">
                              14-Tage-Frist läuft
                            </span>
                            <div className="mt-1 text-[10.5px] text-sand-500">Start erst nach Fristablauf</div>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right font-semibold text-sand-800">
                        {fmtAmount(b.amount_total, b.currency)}
                        {b.discount_pct > 0 && b.subtotal_amount ? (
                          <div className="text-[10.5px] font-normal text-terra">
                            inkl. {b.discount_pct} % Rabatt (−{fmtAmount(b.subtotal_amount - b.amount_total, b.currency)})
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
